

const AZIONE_FILTER_KEY = "filter.p.m.custom.product_finder_azione";
const SUPERFICIE_FILTER_KEY = "filter.p.m.custom.product_finder_label_superficie";
const MATERIALE_FILTER_KEY = "filter.p.m.custom.product_finder_materiale";
const MACCHIA_FILTER_KEY = "filter.p.m.custom.product_finder_macchia";

class UniWizard extends HTMLElement {
  constructor() {
    super();
    this._suppressReactions = false;
  }

  connectedCallback() {
    this.actionDropdown = this.querySelector('smart-dropdown[name="action"]');
    this.supportDropdown = this.querySelector('smart-dropdown[name="support-surface"]');
    this.materialDropdown = this.querySelector('smart-dropdown[name="material"]');
    this.stainDropdown = this.querySelector('smart-dropdown[name="stain"]');
    this.shuffleButton = this.querySelector(".uni-wizard__shuffle");
    this.submitButton = this.querySelector(".uni-wizard__submit");
    this.wizardStarted = false;

    this.stainDropdown.parentElement.style.display = "none";

    this.data = JSON.parse(document.getElementById("wizard-data").innerHTML);

    this._initActionDropdown();
    this._bindEvents();
  }

  _resetDropdown(dropdown, { hideParent = false } = {}) {
    dropdown.reset?.();
    if (hideParent && dropdown.parentElement) {
      dropdown.parentElement.style.display = "none";
    }
  }

  _getDropdownOptions(dropdown) {
    return [...dropdown.querySelectorAll(".sd-list .sd-option")].map((el) => el.dataset.value || el.textContent);
  }

  _randomOf(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  _debounce(fn, delay = 300) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  _initActionDropdown() {
    const actions = this.data?.actions || [];
    this.actionDropdown.setOptions(actions);
  }

  _bindEvents() {
    const safe = (fn) => {
      const debounced = this._debounce((...args) => fn(...args), 300);
      return (...args) => {
        if (this._suppressReactions) return;
        debounced(...args);
      };
    };

    this.actionDropdown.addEventListener(
      "change",
      safe((e) => this._updateSupportOptions(e.target.value)),
    );
    this.supportDropdown.addEventListener(
      "change",
      safe((e) => this._updateMaterialOptions(e.target.value)),
    );
    this.materialDropdown.addEventListener(
      "change",
      safe((e) => this._updateStainOptions(e.target.value)),
    );

    this.submitButton.addEventListener("click", () => this._redirectBasedOnCurrentSelections());
    this.shuffleButton.addEventListener("click", () => this._shuffleSelectionsSafe());
  }

  checkWizardStatus() {
    if (this.wizardStarted === true) return;
    this.pushWizardEvent();
  }

  pushWizardEvent() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'tutorial_begin'
    });

    if (typeof fbq !== 'undefined') {
      fbq('track', 'StartTrial');
    }

    this.wizardStarted = true;
  }

  _updateSupportOptions(selectedActionId) {
    this._resetDropdown(this.supportDropdown);
    this._resetDropdown(this.materialDropdown);
    this._resetDropdown(this.stainDropdown, { hideParent: true });

    const supports = (this.data?.surfaces || []).filter((s) => s.azioneId === selectedActionId);
    this.supportDropdown.setOptions(supports);
    this.checkWizardStatus();
  }

  _updateMaterialOptions(selectedSupportId) {
    const selectedAction = this.actionDropdown.value;

    if (!selectedAction || !selectedSupportId) return;

    this._resetDropdown(this.materialDropdown);
    this._resetDropdown(this.stainDropdown, { hideParent: true });

    const materials = (this.data?.materials || []).filter((m) => m.superfId === selectedSupportId);
    this.materialDropdown.setOptions(materials);
    this.checkWizardStatus();
  }

  _updateStainOptions(selectedMaterialId) {
    const selectedAction = this.actionDropdown.value;
    const selectedSupport = this.supportDropdown.value;

    if (!selectedAction || !selectedSupport || !selectedMaterialId) return;

    this._resetDropdown(this.stainDropdown, { hideParent: true });

    const entries = (this.data?.level4Entries || []).filter(
      (e) => e.materialId === selectedMaterialId && e.tipo === "macchia",
    );
    const stains = entries.map((e) => ({ id: e.id, label: e.label }));

    this.stainDropdown.setOptions(stains);

    if (stains.length) {
      this.stainDropdown.parentElement.style.display = "inline-block";
    }
    this.checkWizardStatus();
  }

  _redirectBasedOnCurrentSelections() {
    const params = new URLSearchParams();

    const az = this.actionDropdown.value;
    const sp = this.supportDropdown.value;
    const ma = this.materialDropdown.value;
    const st = this.stainDropdown.value;

    if (az) {
      params.append(AZIONE_FILTER_KEY, this.data.actions.find((a) => a.id === az).label);
    }
    if (sp) {
      params.append(SUPERFICIE_FILTER_KEY, this.data.surfaces.find((s) => s.id === sp).label);
    }
    if (ma) {
      params.append(MATERIALE_FILTER_KEY, this.data.materials.find((m) => m.id === ma).label);
    }
    if (st) {
      params.append(MACCHIA_FILTER_KEY, this.data.level4Entries.find((e) => e.id === st).label);
    }

    if (!params.toString()) return;

    // Check for single product handle from JSON
    const entries = (this.data?.level4Entries || []).filter((e) => e.materialId === ma);

    if (st) {
      const entry = entries.find((e) => e.id === st && e.tipo === "macchia");
      if (entry && entry.handle) {
        window.location.href = `/products/${entry.handle}`;
        return;
      }
    } else {
      const handleEntries = entries.filter((e) => e.tipo === "handle");
      if (handleEntries.length === 1 && handleEntries[0].handle) {
        window.location.href = `/products/${handleEntries[0].handle}`;
        return;
      }
    }

    // Fallback to collection with filters
    const url = new URL("/collections/tutti-i-prodotti", window.location.origin);
    url.search = params.toString();
    window.location.href = url.toString();
  }

  _shuffleSelectionsSafe() {
    this._suppressReactions = true;
    try {
      let actions = this._getDropdownOptions(this.actionDropdown);

      if (!actions.length) {
        const resActions = this.data?.actions || [];
        this.actionDropdown.setOptions(resActions);
        actions = resActions.map((v) => v.id);
      }

      if (!actions.length) return;

      const randAction = this._randomOf(actions);
      this.actionDropdown.value = randAction;

      this._updateSupportOptions(randAction);

      const supports = this._getDropdownOptions(this.supportDropdown);
      if (!supports.length) return;
      const randSupport = this._randomOf(supports);
      this.supportDropdown.value = randSupport;

      this._updateMaterialOptions(randSupport);

      const materials = this._getDropdownOptions(this.materialDropdown);
      if (!materials.length) return;
      const randMaterial = this._randomOf(materials);
      this.materialDropdown.value = randMaterial;

      this._updateStainOptions(randMaterial);

      const stains = this._getDropdownOptions(this.stainDropdown);

      if (stains.length) {
        const randStain = this._randomOf(stains);
        this.stainDropdown.value = randStain;
        this.stainDropdown.parentElement.style.display = "inline-block";
      } else {
        this.stainDropdown.parentElement.style.display = "none";
      }
    } finally {
      this._suppressReactions = false;
    }

    this.checkWizardStatus();
  }
}

if (!customElements.get("uni-wizard")) {
  customElements.define("uni-wizard", UniWizard);
}
